import {
  GetPlaylist,
  Playlist,
  timeDividerText,
} from "../../../domain/playlist";
import { VoteMode, VoteModes } from "../../../domain/voting";
import React, { useEffect, useRef, useState } from "react";
import { setBackButtonLocation } from "../../../components/navPanel";
import { LoadingMessagePanel } from "../../../components/loadingPanel";
import styled from "styled-components";
import { SongWithVotes } from "../../../domain/song";
import { LoadStatus, LoadStatuses } from "../../common/loading";
import { StatusCodes } from "../../../services/common";
import { RegisterUser } from "../../../services/userService";
import { RegistrationForm } from "../../../components/registrationForm";

export const KioskPlaylist = ({
  getPlaylist,
  voteMode,
  registerUser,
}: {
  getPlaylist: GetPlaylist;
  voteMode: VoteMode;
  registerUser: RegisterUser;
}) => {
  const [loadStatus, setLoadStatus] = useState<LoadStatus<Playlist>>(
    LoadStatuses.UNINITIALIZED,
  );
  const timer = useRef<number | undefined>(undefined);
  const mounted = useRef(true);

  const clearRefreshTimer = () => {
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = undefined;
    }
  };

  const startRefreshTimer = () => {
    if (timer.current == undefined) {
      timer.current = window.setInterval(async () => {
        await refresh();
      }, 10 * 1000);
    }
  };

  async function refresh() {
    try {
      const maybePlaylist = await getPlaylist();
      if (!mounted.current) {
        return false;
      }

      if (maybePlaylist.status == StatusCodes.REGISTRATION_REQUIRED) {
        clearRefreshTimer();
        setLoadStatus(LoadStatuses.REGISTRATION_REQUIRED);
        return false;
      } else if (maybePlaylist.status == StatusCodes.INSUFFICIENT_PERMISSIONS) {
        clearRefreshTimer();
        setLoadStatus(LoadStatuses.PERMISSION_DENIED);
        return false;
      } else if (maybePlaylist.status == StatusCodes.OK) {
        setLoadStatus({
          data: maybePlaylist.value,
          name: "loaded",
        });
      }
    } catch (e) {
      if (!mounted.current) {
        return false;
      }
      console.warn("Refresh failed. Will try again");
    }
    return true;
  }

  async function startLoading() {
    if (!mounted.current) {
      return;
    }

    setLoadStatus(LoadStatuses.LOADING);
    const shouldContinuePolling = await refresh();
    if (mounted.current && shouldContinuePolling) {
      startRefreshTimer();
    }
  }

  // TODO: refactor this loading pattern out. It's always the same
  useEffect(() => {
    setBackButtonLocation("/playlist");
    (async () => {
      await startLoading();
    })();
    return () => {
      mounted.current = false;
      clearRefreshTimer();
    };
  }, []);

  const Panel = () => {
    if (
      loadStatus.name == LoadStatuses.LOADING.name ||
      loadStatus.name == LoadStatuses.UNINITIALIZED.name
    ) {
      return <LoadingMessagePanel />;
    } else if (loadStatus.name == LoadStatuses.REGISTRATION_REQUIRED.name) {
      return (
        <RegistrationForm
          registerUser={registerUser}
          onLogin={() => {
            (async () => {
              await startLoading();
            })();
          }}
        />
      );
    } else if (loadStatus.name == LoadStatuses.PERMISSION_DENIED.name) {
      return (
        <div className="message">
          You do not have permission to view the kiosk.
        </div>
      );
    } else if (loadStatus.name == "loaded" && loadStatus.data) {
      return (
        <>
          <QRCodePanelDiv id={"qr-code"} />
          {loadStatus.data.songs.page.length == 0 ? (
            <div className="message">
              The playlist is empty. Add some songs!
            </div>
          ) : (
            <PlaylistView playlist={loadStatus.data} voteMode={voteMode} />
          )}
          <KioskFooter>
            Songs will be limited to 3 minutes in length
          </KioskFooter>
        </>
      );
    }
    return null;
  };
  return (
    <>
      <Panel />
    </>
  );
};

const PlaylistView = ({
  playlist,
  voteMode,
}: {
  playlist: Playlist;
  voteMode: VoteMode;
}) => {
  const voteColumnText = voteMode == VoteModes.SINGLE_VOTE ? "Votes" : "Bids";
  return (
    <>
      <div className="playlist-kiosk">
        <SongRow>
          <TableColumnHeader className="left">Title</TableColumnHeader>
          <TableColumnHeader>Requester</TableColumnHeader>
          <TableColumnHeader>{voteColumnText}</TableColumnHeader>
        </SongRow>
        <SongsPanel role={"list"} aria-label={"tag-list"}>
          {playlist.songs.page.map((song, index) => (
            <React.Fragment key={song.id}>
              {index == playlist.timeDividerIndex && (
                <TimeDivider role={"separator"} aria-label={"time-divider"}>
                  {timeDividerText}
                </TimeDivider>
              )}
              <SongView song={song} voteMode={voteMode} />
            </React.Fragment>
          ))}
        </SongsPanel>
      </div>
    </>
  );
};

// noinspection CssUnknownTarget
const QRCodePanelDiv = styled.div`
  position: fixed;
  top: 1vh;
  right: 5vh;
  height: 12vh;
  width: 12vh;
  aspect-ratio: 1 / 1;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url("./qrcode_yersong.jojofest.org.png");
  pointer-events: none;
`;

const ListItem = styled.div`
  height: 6vh;

  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
  padding: 1vh 0 1vh 0;
  display: flex;
  flex-direction: row;
`;

const TimeDivider = styled.div`
  background-color: #44006f;
  color: #ce9eff;
  font-size: 2.5vh;
  font-style: italic;
  padding: 0.5vh 1vh;
`;

// Pinned to the screen like the QR code, so it stays put whatever the list length.
const KioskFooter = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #44006f;
  color: #ce9eff;
  font-size: 2.5vh;
  font-style: italic;
  text-align: left;
  padding: 0.5vh 3vh;
`;

const SongsPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 90%;
`;

const SongRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  grid-column-gap: 3vh;
  width: 100%;
  text-align: center;
  padding: 1vh;
`;

const SongTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableColumnHeader = styled.div`
  font-weight: bold;
  text-decoration: underline;
`;

const SongView = ({
  song,
  voteMode,
}: {
  song: SongWithVotes;
  voteMode: VoteMode;
}) => {
  const voterName =
    song.voters && song.voters.length > 0 ? song.voters[0].name : undefined;

  const totalBidsSymbol = voteMode == VoteModes.SINGLE_VOTE ? "" : "$";
  const lockedForPlay = <div>&#127925;</div>;
  const showingVotes = (
    <div>
      {totalBidsSymbol}
      {song.voteCount}
    </div>
  );
  return (
    <ListItem
      role={"listitem"}
      key={`song::${song.id}`}
      aria-label={`song: ${song.title}`}
      data-id={song.id}
    >
      <SongRow>
        <SongTitle className="left">{song.title}</SongTitle>
        <div>{voterName}</div>
        <div>{song.lockOrder > 0 ? lockedForPlay : showingVotes}</div>
      </SongRow>
    </ListItem>
  );
};
